/**
 * SupportPopup — "Soutenez l'application" modal.
 * Auto-opens once per browser session on the home page, and can be reopened
 * anytime via the custom event `hypnose-open-support` (support banner).
 * Actions: don PayPal, partage WhatsApp, Instagram, partage natif.
 */
import React, { useEffect, useState } from 'react';
import { X, Heart, Share2, Instagram, MessageCircle, Facebook, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { PLAY_STORE_URL, SHARE_TEXT, shareAppWithImage, shareUrls } from '../services/shareApp';

const API = process.env.REACT_APP_BACKEND_URL;
const POPUP_KEY = 'hypnose_support_popup_shown_v1';
const APP_URL = PLAY_STORE_URL;

const whatsappUrl = shareUrls.whatsapp;
const DONATION_PRESETS = [5, 10, 20];

export function BetaTesterPopup() {
  const [open, setOpen] = useState(false);
  const [paypalValue, setPaypalValue] = useState(null);

  useEffect(() => {
    // Same PayPal link as the "Soutenir" page (configured in the donations admin)
    if (!open) return;
    axios.get(`${API}/api/donation-methods`)
      .then((res) => {
        const pp = (res.data || []).find((m) => m.type === 'paypal' && m.enabled && m.value);
        if (pp) setPaypalValue(pp.value);
      })
      .catch(() => {});
  }, [open]);

  const openPaypal = (amount) => {
    if (!paypalValue) return;
    const sep = paypalValue.includes('?') ? '&' : '?';
    const url = amount ? `${paypalValue}${sep}amount=${amount}` : paypalValue;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const openNow = () => setOpen(true);
    window.addEventListener('hypnose-open-support', openNow);
    return () => window.removeEventListener('hypnose-open-support', openNow);
  }, []);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(POPUP_KEY) === '1') return;
    } catch { /* noop */ }

    // Only show the popup when the home page is actually visible.
    let timerId = null;
    let intervalId = null;
    const isSessionActive = () => {
      try {
        if (document.querySelector('[data-testid$="-mode"]')) return true;
        const blocking = [
          'affirmations-panel', 'yoga-panel', 'stretching-panel',
          'stats-panel', 'ad-settings', 'session-audio-panel'
        ];
        return blocking.some((id) => document.querySelector(`[data-testid="${id}"]`));
      } catch { return false; }
    };
    const tryOpen = () => {
      try { if (sessionStorage.getItem(POPUP_KEY) === '1') return true; } catch { /* noop */ }
      if (!isSessionActive()) { setOpen(true); return true; }
      return false;
    };
    timerId = setTimeout(() => {
      if (tryOpen()) return;
      intervalId = setInterval(() => { if (tryOpen()) clearInterval(intervalId); }, 3000);
    }, 6000);
    return () => {
      if (timerId) clearTimeout(timerId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const close = () => {
    try { sessionStorage.setItem(POPUP_KEY, '1'); } catch { /* noop */ }
    setOpen(false);
  };

  const shareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_TEXT);
      toast.success('Message copié ! Collez-le dans votre story ou bio Instagram 📸');
    } catch {
      toast.info('Copiez le lien : ' + APP_URL);
    }
    window.open('https://www.instagram.com/', '_blank', 'noopener');
  };

  const shareNative = async () => {
    await shareAppWithImage();
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center bg-black/85 p-4"
      style={{ transform: 'translateZ(0)', isolation: 'isolate' }}
      data-testid="support-popup"
      onClick={close}
    >
      <div
        className="relative max-w-sm w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto rounded-3xl bg-slate-950 border border-amber-500/30 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white/70 flex items-center justify-center"
          data-testid="support-popup-close"
        >
          <X size={16} />
        </button>
        <div className="text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center mb-3">
            <Heart className="w-7 h-7 text-amber-300" />
          </div>
          <h3
            className="text-2xl text-amber-300 font-semibold mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Soutenez l&apos;application
          </h3>
          <p className="text-white/75 text-sm leading-relaxed mb-4">
            Hypnose clé est <b className="text-amber-200">100% gratuite, sans abonnement</b> —
            créée par une seule personne, avec des centaines d&apos;heures de travail et d&apos;amour.
            Votre soutien finance l&apos;hébergement, les voix et les nouvelles séances.
            Un don ou un simple partage fait toute la différence 💛
          </p>
          <div className="flex flex-col gap-2">
            {paypalValue && (
              <div className="space-y-2" data-testid="support-donate-block">
                <p className="text-white/55 text-[10px] uppercase tracking-wider font-semibold">Faire un don — garder l&apos;appli gratuite</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {DONATION_PRESETS.map((amt) => (
                    <button
                      key={amt}
                      onClick={() => openPaypal(amt)}
                      className="py-2 rounded-full bg-amber-500/20 border border-amber-400/50 text-amber-300 text-sm font-bold hover:bg-amber-500/30 active:scale-95 transition"
                      data-testid={`support-donate-amount-${amt}`}
                    >
                      {amt}&nbsp;€
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => openPaypal(null)}
                  className="w-full px-5 py-3 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold shadow-lg shadow-amber-500/30 transition text-sm flex items-center justify-center gap-2"
                  data-testid="support-donate-btn"
                >
                  <Heart size={16} /> Autre montant
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2.5 rounded-full bg-emerald-600/90 hover:bg-emerald-500 text-white font-medium text-xs flex items-center justify-center gap-1.5 transition"
                data-testid="support-whatsapp-btn"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <button
                onClick={shareInstagram}
                className="px-3 py-2.5 rounded-full text-white font-medium text-xs flex items-center justify-center gap-1.5 transition hover:opacity-90"
                style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
                data-testid="support-instagram-btn"
              >
                <Instagram size={14} /> Instagram
              </button>
              <a
                href={shareUrls.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2.5 rounded-full bg-blue-600/90 hover:bg-blue-500 text-white font-medium text-xs flex items-center justify-center gap-1.5 transition"
                data-testid="support-facebook-btn"
              >
                <Facebook size={14} /> Facebook
              </a>
              <a
                href={shareUrls.sms}
                className="px-3 py-2.5 rounded-full bg-teal-600/90 hover:bg-teal-500 text-white font-medium text-xs flex items-center justify-center gap-1.5 transition"
                data-testid="support-sms-btn"
              >
                <MessageSquare size={14} /> SMS
              </a>
            </div>
            <button
              onClick={shareNative}
              className="px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white/90 font-medium text-xs flex items-center justify-center gap-2 transition"
              data-testid="support-share-btn"
            >
              <Share2 size={14} /> Partager l&apos;application (image + lien)
            </button>
            <button
              onClick={close}
              className="text-xs text-white/55 hover:text-white/80 py-1"
              data-testid="support-popup-later"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BetaTesterPopup;
